import { Document, Page, pdf, Text, View } from "@react-pdf/renderer"
import { styles, TableCell } from "components"
import moment from "moment"
import { cellValuePdf, RowExportTypePdf } from "types"
import { convertNumber } from "utils"
import { calculateSummary } from "../functions"
import { DataList, Summary } from "../types"

type UseExportPdf = {
  data: DataList[]
  dateFilter: string
}

export const useExportPdf = ({
  data,
  dateFilter
}: UseExportPdf) => {

  const columns = [
    { header: "NO", key: "no", width: 5 },
    { header: "BILL ID#", key: "bill_id", width: 25 },
    { header: "REF PO ID#", key: "ref_po_id", width: 25 },
    { header: "DATE", key: "date", width: 15 },
    { header: "VENDOR", key: "vendor", width: 15 },
    { header: "TOTAL", key: "total", width: 15 },
    { header: "7 DAYS ", key: "7days", width: 15 },
    { header: "15 DAYS ", key: "15days", width: 15 },
    { header: "21 DAYS ", key: "21days", width: 15 },
    { header: "30 DAYS ", key: "30days", width: 15 },
    { header: "60+ DAYS ", key: "60days", width: 15 }
  ]

  function numberFormat(value: number): cellValuePdf {
    return {
      text: convertNumber(value).intoCurrency,
      style: {
        textAlign: "right",
        fontWeight: "bold"
      }
    }
  }

  const calculate = calculateSummary(data)

  const dataMap: RowExportTypePdf[] = data.map((item, index) => {
    return {
      no: (index + 1).toString(),
      bill_id: item.trx_code,
      ref_po_id: item.trx_code_po,
      date: moment(item.transaction_date).format("DD/MM/YYYY"),
      vendor: item.vendor_name,
      total: numberFormat(item.total_amount),
      "7days": item?.aged_payable <= 7 ? numberFormat(item?.balance) : "-",
      "15days": item?.aged_payable >= 8 && item?.aged_payable <= 15 ? numberFormat(item?.balance) : "-",
      "21days": item?.aged_payable >= 16 && item?.aged_payable <= 21 ? numberFormat(item?.balance) : "-",
      "30days": item?.aged_payable >= 22 && item?.aged_payable <= 30 ? numberFormat(item?.balance) : "-",
      "60days": item?.aged_payable >= 31 ? numberFormat(item?.balance) : "-",
    }
  })

  return {
    handleExportPdf: () =>
      pdf(
        <PdfTemplate
          data={dataMap}
          column={columns}
          date={dateFilter}
          calculate={calculate}
        />
      )
        .toBlob()
        .then((blob) => {
          const url = URL.createObjectURL(blob)
          const link = document.createElement("a")
          link.href = url
          link.download = `register-aged-payable-${dateFilter}.pdf`
          link.click()
          URL.revokeObjectURL(url)
        })
        .catch((error) => {
          console.error("Gagal membuat PDF:", error)
        })
  }
}

function PdfTemplate({
  data,
  column,
  date,
  calculate
}: {
  data: RowExportTypePdf[]
  column: { header: string; key: string; width: number }[]
  date?: string
  calculate: Summary
}) {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>PURCHASE</Text>
        <Text style={styles.title}>REGISTER AGED PAYABLE</Text>
        {date && <Text style={styles.date}>Date: {date}</Text>}
        <TableCell data={data} column={column} />
        <View
          style={{
            marginTop: 10,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            alignItems: "flex-end"
          }}>
          <View
            style={[
              styles.date,
              {
                display: "flex",
                flexDirection: "row",
                fontSize: 8
              }
            ]}>
            <Text>SUMMARY TOTAL : </Text>

            <Text style={{ width: 50, textAlign: "right", paddingLeft: 2 }}>
              {convertNumber(calculate.balance).intoCurrency}
            </Text>
          </View>
          <View
            style={[
              styles.date,
              {
                display: "flex",
                flexDirection: "row",
                fontSize: 8
              }
            ]}>
            <Text>SUMMARY 7 DAYS : </Text>
            <Text style={{ width: 50, textAlign: "right", paddingLeft: 2 }}>
              {convertNumber(calculate["7_day"]).intoCurrency}
            </Text>
          </View>

          <View
            style={[
              styles.date,
              {
                display: "flex",
                flexDirection: "row",
                fontSize: 8
              }
            ]}>
            <Text>SUMMARY 15 DAYS : </Text>
            <Text style={{ width: 50, textAlign: "right", paddingLeft: 2 }}>
              {convertNumber(calculate["15_day"]).intoCurrency}
            </Text>
          </View>

          <View
            style={[
              styles.date,
              {
                display: "flex",
                flexDirection: "row",
                fontSize: 8
              }
            ]}>
            <Text>SUMMARY 21 DAYS : </Text>
            <Text style={{ width: 50, textAlign: "right", paddingLeft: 2 }}>
              {convertNumber(calculate["21_day"]).intoCurrency}
            </Text>
          </View>

          <View
            style={[
              styles.date,
              {
                display: "flex",
                flexDirection: "row",
                fontSize: 8
              }
            ]}>
            <Text>SUMMARY 30 DAYS : </Text>
            <Text style={{ width: 50, textAlign: "right", paddingLeft: 2 }}>
              {convertNumber(calculate["30_day"]).intoCurrency}
            </Text>
          </View>

          <View
            style={[
              styles.date,
              {
                display: "flex",
                flexDirection: "row",
                fontSize: 8
              }
            ]}>
            <Text>SUMMARY 60+ DAYS : </Text>
            <Text style={{ width: 50, textAlign: "right", paddingLeft: 2 }}>
              {convertNumber(calculate["60_day"]).intoCurrency}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  )
}
